/**
 * Canonical month calculation functions.
 *
 * ALL month calculations across the app should use these functions.
 * Do NOT use dayjs().diff() directly for month calculations elsewhere.
 */
import dayjs, { Dayjs } from 'dayjs'

/**
 * Calculate inclusive calendar months a product spans.
 *
 * This is the single source of truth for "how many months" a product is billed for.
 * Counts the number of distinct calendar months the date range touches.
 *
 * Examples:
 *   Mar 1 → Mar 1 (next year) = 13 (Mar thru Mar inclusive)
 *   Mar 1 → Apr 1             = 2  (active in March AND April)
 *   Mar 1 → Mar 28            = 1  (same month)
 *   Mar 15 → Apr 14           = 2  (spans March and April)
 *   Mar 1 → Mar 1             = 1  (same day = 1 month)
 */
export const calculateMonthsBetween = (
  startDate: string | Date | Dayjs,
  endDate: string | Date | Dayjs
): number => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  if (!start.isValid() || !end.isValid()) return 0

  // Inclusive calendar month count: how many distinct months does this range touch?
  const monthDiff = (end.year() - start.year()) * 12 + (end.month() - start.month())
  return monthDiff + 1
}

/**
 * Get months for a date range, with fallback to 1.
 * Use this when you need "at least 1 month" (e.g., for product qty).
 */
export const monthsFor = (startISO?: string, endISO?: string): number => {
  if (!startISO || !endISO) return 1
  const months = calculateMonthsBetween(startISO, endISO)
  return months || 1
}
